import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Table, Row, Col, Statistic, Alert, DatePicker, message, Modal, Spin } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FileTextOutlined, FilePdfOutlined, RobotOutlined } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import ApiService from '../services/api';
import jsPDF from 'jspdf';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditTrail = () => {
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [expensesData, setExpensesData] = useState(null);
  const [fuelData, setFuelData] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditView, setAuditView] = useState('month');
  const [userType, setUserType] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [analysisModal, setAnalysisModal] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);

  useEffect(() => {
    getCurrentUser();
    loadDrivers();
  }, []);

  const getCurrentUser = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const type = user.attributes['custom:user_type'];
      setUserType(type);
    } catch (error) {
      console.error('Error getting user:', error);
    }
  };

  const loadDrivers = async () => {
    try {
      const data = await ApiService.getDashboardData();
      setDrivers(data.drivers || []);
      setTrucks(data.trucks || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  };

  const generateReports = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a date range');
      return;
    }
    
    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      
      const isAdmin = userType === 'admin';
      const [revenue, expenses, fuel] = await Promise.all([
        ApiService.getGrossIncomeReport(null, startDate, endDate, selectedTruck, isAdmin),
        ApiService.getExpensesReport(null, startDate, endDate, selectedTruck, isAdmin),
        ApiService.getFuelConsumptionReport(null, startDate, endDate, selectedTruck, isAdmin)
      ]);
      
      setRevenueData(revenue);
      setExpensesData(expenses);
      setFuelData(fuel);
      generateAuditData(revenue, expenses, fuel, startDate, endDate);
    } catch (error) {
      console.error('Error generating reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAuditData = (revenue, expenses, fuel, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const monthlyData = generateMonthlyAudit(revenue, expenses, fuel, start, end);
    const weeklyData = generateWeeklyAudit(revenue, expenses, fuel, start, end);
    const dailyData = generateDailyAudit(revenue, expenses, fuel, start, end);
    
    setAuditData({
      month: monthlyData,
      week: weeklyData,
      day: dailyData
    });
  };

  const generateMonthlyAudit = (revenue, expenses, fuel, start, end) => {
    const trips = revenue?.trips || [];
    const fuelRecords = expenses?.fuel_records || [];
    const expenseRecords = expenses?.expense_records || [];
    
    const monthlyData = {};
    
    trips.forEach(trip => {
      const month = trip.trip_date?.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      monthlyData[month].revenue += trip.total_revenue || 0;
    });
    
    fuelRecords.forEach(record => {
      const month = record.date?.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      monthlyData[month].fuel += record.amount_spent || 0;
    });
    
    expenseRecords.forEach(record => {
      const month = record.date?.slice(0, 7);
      if (!monthlyData[month]) {
        monthlyData[month] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      monthlyData[month].expenses += record.amount || 0;
    });
    
    return Object.entries(monthlyData).map(([month, data]) => ({
      period: month,
      name: new Date(month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
      revenue: data.revenue,
      expenses: data.expenses,
      fuel: data.fuel
    }));
  };

  const generateWeeklyAudit = (revenue, expenses, fuel, start, end) => {
    const trips = revenue?.trips || [];
    const fuelRecords = expenses?.fuel_records || [];
    const expenseRecords = expenses?.expense_records || [];
    
    const weeklyData = {};
    
    const getWeekKey = (date) => {
      const d = new Date(date);
      const week = Math.ceil(d.getDate() / 7);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-W${week}`;
    };
    
    trips.forEach(trip => {
      const week = getWeekKey(trip.trip_date);
      if (!weeklyData[week]) {
        weeklyData[week] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      weeklyData[week].revenue += trip.total_revenue || 0;
    });
    
    fuelRecords.forEach(record => {
      const week = getWeekKey(record.date);
      if (!weeklyData[week]) {
        weeklyData[week] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      weeklyData[week].fuel += record.amount_spent || 0;
    });
    
    expenseRecords.forEach(record => {
      const week = getWeekKey(record.date);
      if (!weeklyData[week]) {
        weeklyData[week] = { revenue: 0, expenses: 0, fuel: 0 };
      }
      weeklyData[week].expenses += record.amount || 0;
    });
    
    return Object.entries(weeklyData).map(([week, data]) => ({
      period: week,
      name: `Week ${week.split('-W')[1]} of ${week.split('-')[1]}/${week.split('-')[0]}`,
      revenue: data.revenue,
      expenses: data.expenses,
      fuel: data.fuel
    }));
  };

  const generateDailyAudit = (revenue, expenses, fuel, start, end) => {
    const trips = revenue?.trips || [];
    const fuelRecords = expenses?.fuel_records || [];
    const expenseRecords = expenses?.expense_records || [];
    
    const dailyData = {};
    
    trips.forEach(trip => {
      const day = trip.trip_date;
      if (!dailyData[day]) {
        dailyData[day] = { revenue: 0, expenses: 0, fuel: 0, trips: [], fuelRecords: [], expenseRecords: [], truck_id: null };
      }
      dailyData[day].revenue += trip.total_revenue || 0;
      // Add each individual trip with truck_id from parent record
      if (trip.trips && Array.isArray(trip.trips)) {
        trip.trips.forEach(individualTrip => {
          dailyData[day].trips.push({ ...individualTrip, trip_truck_id: trip.truck_id, driver_name: trip.driver_name });
        });
      }
      if (!dailyData[day].truck_id && trip.truck_id) {
        dailyData[day].truck_id = trip.truck_id;
      }
    });
    
    fuelRecords.forEach(record => {
      const day = record.date;
      if (!dailyData[day]) {
        dailyData[day] = { revenue: 0, expenses: 0, fuel: 0, trips: [], fuelRecords: [], expenseRecords: [], truck_id: null };
      }
      dailyData[day].fuel += record.amount_spent || 0;
      dailyData[day].fuelRecords.push(record);
      if (!dailyData[day].truck_id && record.truck_id) {
        dailyData[day].truck_id = record.truck_id;
      }
    });
    
    expenseRecords.forEach(record => {
      const day = record.date;
      if (!dailyData[day]) {
        dailyData[day] = { revenue: 0, expenses: 0, fuel: 0, trips: [], fuelRecords: [], expenseRecords: [], truck_id: null };
      }
      dailyData[day].expenses += record.amount || 0;
      dailyData[day].expenseRecords.push(record);
      if (!dailyData[day].truck_id && record.truck_id) {
        dailyData[day].truck_id = record.truck_id;
      }
    });
    
    return Object.entries(dailyData).map(([day, data]) => ({
      period: day,
      name: new Date(day).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      revenue: data.revenue,
      expenses: data.expenses,
      fuel: data.fuel,
      trips: data.trips,
      fuelRecords: data.fuelRecords,
      expenseRecords: data.expenseRecords,
      truck_id: data.truck_id
    }));
  };

  const downloadPeriodPDF = async () => {
    if (!auditData || !auditData[auditView] || auditData[auditView].length === 0) {
      message.error('No data available for the selected period');
      return;
    }

    setPdfLoading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      let yPosition = 20;
      const data = auditData[auditView];
      
      const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
      const totalFuel = data.reduce((sum, item) => sum + item.fuel, 0);
      const totalExpenses = data.reduce((sum, item) => sum + item.expenses, 0);
      const netProfit = totalRevenue - totalFuel - totalExpenses;

      const startDate = dateRange ? dateRange[0].format('YYYY-MM-DD') : 'N/A';
      const endDate = dateRange ? dateRange[1].format('YYYY-MM-DD') : 'N/A';
      const selectedTruckName = selectedTruck ? 
        trucks.find(t => t.truck_id === selectedTruck)?.license_plate || 'Unknown Truck' : 'All Trucks';
      const driverName = selectedTruck ? 
        drivers.find(d => d.truck_id === selectedTruck)?.name || '' : 'All Drivers';
      
      // Add logo
      try {
        const logoImg = new Image();
        logoImg.src = '/logo.png';
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          logoImg.onerror = resolve;
        });
        if (logoImg.complete && logoImg.naturalHeight !== 0) {
          const logoWidth = 40;
          const logoX = (pageWidth - logoWidth) / 2;
          pdf.addImage(logoImg, 'PNG', logoX, 10, logoWidth, 15);
          yPosition = 30;
        }
      } catch (error) {
        console.log('Logo not loaded, continuing without it');
      }
      
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${auditView === 'day' ? 'Daily' : auditView === 'week' ? 'Weekly' : 'Monthly'} Operations Report`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} - ${new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, yPosition);
      yPosition += 8;
      pdf.text(`Scope: ${selectedTruckName}${driverName !== 'All Drivers' ? ` (${driverName})` : ''}`, 20, yPosition);
      yPosition += 8;
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, 20, yPosition);
      yPosition += 20;
      
      pdf.setLineWidth(0.5);
      pdf.line(20, yPosition - 5, pageWidth - 20, yPosition - 5);
      yPosition += 10;

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PERIOD SUMMARY', 20, yPosition);
      yPosition += 15;
      
      const boxWidth = 40;
      const boxHeight = 20;
      const boxSpacing = 45;
      const startX = 20;
      
      pdf.rect(startX, yPosition, boxWidth, boxHeight);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Revenue', startX + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${totalRevenue.toFixed(2)}`, startX + 2, yPosition + 15);
      
      pdf.rect(startX + boxSpacing, yPosition, boxWidth, boxHeight);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Total Expenses', startX + boxSpacing + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${(totalFuel + totalExpenses).toFixed(2)}`, startX + boxSpacing + 2, yPosition + 15);
      
      pdf.setLineWidth(1);
      pdf.rect(startX + (boxSpacing * 2), yPosition, boxWidth, boxHeight);
      pdf.setLineWidth(0.5);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Net Profit', startX + (boxSpacing * 2) + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`$${netProfit.toFixed(2)}`, startX + (boxSpacing * 2) + 2, yPosition + 15);
      
      const profitMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(2) : 0;
      pdf.rect(startX + (boxSpacing * 3), yPosition, boxWidth, boxHeight);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Profit Margin', startX + (boxSpacing * 3) + 2, yPosition + 8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${profitMargin}%`, startX + (boxSpacing * 3) + 2, yPosition + 15);
      
      yPosition += boxHeight + 20;

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('FINANCIAL BREAKDOWN', 20, yPosition);
      yPosition += 15;
      
      const tableStartY = yPosition;
      const colWidths = [60, 30, 30, 30, 40];
      const colX = [20, 80, 110, 140, 170];
      const rowHeight = 8;
      
      pdf.setFillColor(245, 245, 245);
      pdf.rect(20, yPosition, 170, rowHeight, 'F');
      pdf.rect(20, yPosition, 170, rowHeight);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Period', colX[0] + 2, yPosition + 6);
      pdf.text('Revenue', colX[1] + 2, yPosition + 6);
      pdf.text('Fuel', colX[2] + 2, yPosition + 6);
      pdf.text('Expenses', colX[3] + 2, yPosition + 6);
      pdf.text('Net Profit', colX[4] + 2, yPosition + 6);
      
      for (let i = 1; i < colX.length; i++) {
        pdf.line(colX[i], yPosition, colX[i], yPosition + rowHeight);
      }
      yPosition += rowHeight;
      
      pdf.setFont('helvetica', 'normal');
      data.forEach((item, index) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        const itemProfit = item.revenue - item.fuel - item.expenses;
        
        if (index % 2 === 1) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(20, yPosition, 170, rowHeight, 'F');
        }
        
        pdf.rect(20, yPosition, 170, rowHeight);
        for (let i = 1; i < colX.length; i++) {
          pdf.line(colX[i], yPosition, colX[i], yPosition + rowHeight);
        }
        
        pdf.text(item.name.length > 18 ? item.name.substring(0, 18) + '...' : item.name, colX[0] + 2, yPosition + 6);
        pdf.text(`$${item.revenue.toFixed(2)}`, colX[1] + 2, yPosition + 6);
        pdf.text(`$${item.fuel.toFixed(2)}`, colX[2] + 2, yPosition + 6);
        pdf.text(`$${item.expenses.toFixed(2)}`, colX[3] + 2, yPosition + 6);
        pdf.text(`$${itemProfit.toFixed(2)}`, colX[4] + 2, yPosition + 6);
        yPosition += rowHeight;
      });
      
      pdf.setFillColor(240, 240, 240);
      pdf.rect(20, yPosition, 170, rowHeight, 'F');
      pdf.setLineWidth(1);
      pdf.rect(20, yPosition, 170, rowHeight);
      pdf.setLineWidth(0.5);
      
      for (let i = 1; i < colX.length; i++) {
        pdf.line(colX[i], yPosition, colX[i], yPosition + rowHeight);
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('TOTALS', colX[0] + 2, yPosition + 6);
      pdf.text(`$${totalRevenue.toFixed(2)}`, colX[1] + 2, yPosition + 6);
      pdf.text(`$${totalFuel.toFixed(2)}`, colX[2] + 2, yPosition + 6);
      pdf.text(`$${totalExpenses.toFixed(2)}`, colX[3] + 2, yPosition + 6);
      pdf.text(`$${netProfit.toFixed(2)}`, colX[4] + 2, yPosition + 6);

      const fileName = `${auditView === 'day' ? 'Daily' : auditView === 'week' ? 'Weekly' : 'Monthly'}_Report_${startDate}_to_${endDate}.pdf`;
      pdf.save(fileName);
      message.success('Period report downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const generateAIAnalysis = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a date range first');
      return;
    }
    
    setAnalysisLoading(true);
    setAnalysisModal(true);
    setAnalysisData(null);
    
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const response = await ApiService.getAIAnalysis(startDate, endDate, selectedTruck);
      setAnalysisData(response);
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      message.error('Failed to generate AI analysis');
      setAnalysisModal(false);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const formatAnalysis = (text) => {
    if (!text) return null;
    
    const sections = text.split(/\n\n+/);
    return sections.map((section, idx) => {
      const lines = section.split('\n');
      const isHeading = lines[0].match(/^\*\*(.+)\*\*:?$/);
      
      if (isHeading) {
        return (
          <div key={idx} style={{ marginBottom: 16 }}>
            <h4 style={{ color: '#1890ff', marginBottom: 8 }}>{isHeading[1]}</h4>
            <div style={{ paddingLeft: 16 }}>
              {lines.slice(1).map((line, i) => (
                <p key={i} style={{ marginBottom: 4 }}>{line.replace(/^[•\-]\s*/, '• ')}</p>
              ))}
            </div>
          </div>
        );
      }
      
      return <p key={idx} style={{ marginBottom: 8 }}>{section}</p>;
    });
  };

  const currentData = auditData?.[auditView] || [];
  
  const columns = [
    { title: 'Period', dataIndex: 'name', key: 'name' },
    { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: (val) => `$${val}` },
    { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (val) => `$${val}` },
    { title: 'Fuel Cost', dataIndex: 'fuel', key: 'fuel', render: (val) => `$${val}` },
    { title: 'Net Profit', key: 'profit', render: (_, record) => {
      const profit = record.revenue - record.expenses - record.fuel;
      return (
        <span style={{ color: profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
          ${profit}
        </span>
      );
    }}
  ];

  const chartData = currentData.map(item => ({
    name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
    revenue: item.revenue,
    expenses: item.expenses + item.fuel,
    profit: item.revenue - item.expenses - item.fuel
  }));

  return (
    <div>
      <h1>Operations Report</h1>
      
      <Alert
        message="Operations Report"
        description="Detailed breakdown of all business operations. Track revenue, expenses, and operational data by time period."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Select
              placeholder="All Trucks"
              value={selectedTruck}
              onChange={setSelectedTruck}
              style={{ width: 250 }}
              allowClear
            >
              {trucks.map(truck => {
                const driver = drivers.find(d => d.truck_id === truck.truck_id);
                return (
                  <Option key={truck.truck_id} value={truck.truck_id}>
                    {truck.license_plate} {driver ? `(${driver.name})` : ''}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col>
            <RangePicker 
              value={dateRange} 
              onChange={setDateRange}
              placeholder={['Start Date', 'End Date']}
              style={{ borderColor: !dateRange ? '#ff4d4f' : undefined }}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={generateReports} loading={loading}>
              Generate Reports
            </Button>
          </Col>
        </Row>
      </Card>

      {auditData && (
        <>
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col>
                <Select value={auditView} onChange={setAuditView} style={{ width: 120 }}>
                  <Option value="month">Monthly</Option>
                  <Option value="week">Weekly</Option>
                  <Option value="day">Daily</Option>
                </Select>
              </Col>
              <Col>
                <Button 
                  type="primary" 
                  icon={<FilePdfOutlined />} 
                  onClick={downloadPeriodPDF}
                  loading={pdfLoading}
                >
                  Download Period Report
                </Button>
              </Col>
              <Col>
                <Button 
                  type="default" 
                  icon={<RobotOutlined />} 
                  onClick={() => generateAIAnalysis()}
                  loading={analysisLoading}
                >
                  AI Analysis
                </Button>
              </Col>
            </Row>
          </Card>

          <Row gutter={16} style={{ marginBottom: 24 }}>          
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Total Revenue" 
                  value={currentData.reduce((sum, item) => sum + item.revenue, 0)} 
                  prefix="$" 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Total Expenses" 
                  value={currentData.reduce((sum, item) => sum + item.expenses + item.fuel, 0)} 
                  prefix="$" 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Net Profit" 
                  value={currentData.reduce((sum, item) => sum + item.revenue - item.expenses - item.fuel, 0)} 
                  prefix="$" 
                  valueStyle={{ color: currentData.reduce((sum, item) => sum + item.revenue - item.expenses - item.fuel, 0) >= 0 ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic 
                  title="Records" 
                  value={currentData.length} 
                />
              </Card>
            </Col>
          </Row>

          <Card title={`${auditView.charAt(0).toUpperCase() + auditView.slice(1)}ly Performance`} style={{ marginBottom: 24 }}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue" fill="#52c41a" name="Revenue" />
                <Bar dataKey="expenses" fill="#ff4d4f" name="Expenses" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Detailed Breakdown">
            <Table 
              dataSource={currentData} 
              columns={columns} 
              rowKey="period"
              pagination={{ pageSize: 10 }}
              expandable={auditView === 'day' ? {
                expandedRowRender: (record) => (
                  <div style={{ padding: '16px' }}>
                    {record.trips && record.trips.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <h4>Trips ({record.trips.length})</h4>
                        <Table
                          dataSource={record.trips}
                          rowKey={(trip, index) => `${trip.trip_id}-${index}`}
                          columns={[
                            { title: 'Origin', dataIndex: 'origin', key: 'origin' },
                            { title: 'Destination', dataIndex: 'destination', key: 'destination' },
                            { title: 'Price', dataIndex: 'price', key: 'price', render: (val) => `$${val || 0}`, width: 80 },
                            { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' },
                            { title: 'Truck', key: 'truck', render: (_, trip) => {
                              const truck = trucks.find(t => t.truck_id === trip.trip_truck_id);
                              return truck?.license_plate || trip.trip_truck_id || '-';
                            }, width: 100 }
                          ]}
                          pagination={false}
                          size="small"
                        />
                      </div>
                    )}
                    {record.fuelRecords && record.fuelRecords.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <h4>Fuel Records ({record.fuelRecords.length})</h4>
                        <Table
                          dataSource={record.fuelRecords}
                          rowKey="fuel_id"
                          columns={[
                            { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' },
                            { title: 'Truck', key: 'truck', render: (_, fuel) => {
                              const truck = trucks.find(t => t.truck_id === fuel.truck_id);
                              return truck?.license_plate || fuel.truck_id || '-';
                            }, width: 100 },
                            { title: 'Liters', dataIndex: 'liters', key: 'liters', width: 80 },
                            { title: 'Amount', dataIndex: 'amount_spent', key: 'amount_spent', render: (val) => `$${val || 0}`, width: 80 },
                            { title: 'Odometer', dataIndex: 'odometer', key: 'odometer', width: 100 },
                            { title: 'Status', dataIndex: 'status', key: 'status', width: 80 }
                          ]}
                          pagination={false}
                          size="small"
                        />
                      </div>
                    )}
                    {record.expenseRecords && record.expenseRecords.length > 0 && (
                      <div>
                        <h4>Other Expenses ({record.expenseRecords.length})</h4>
                        <Table
                          dataSource={record.expenseRecords}
                          rowKey="expense_id"
                          columns={[
                            { title: 'Driver', dataIndex: 'driver_name', key: 'driver_name' },
                            { title: 'Truck', key: 'truck', render: (_, expense) => {
                              const truck = trucks.find(t => t.truck_id === expense.truck_id);
                              return truck?.license_plate || expense.truck_id || '-';
                            }, width: 100 },
                            { title: 'Type', dataIndex: 'expense_type', key: 'expense_type' },
                            { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `$${val || 0}`, width: 80 },
                            { title: 'Description', dataIndex: 'description', key: 'description' }
                          ]}
                          pagination={false}
                          size="small"
                        />
                      </div>
                    )}
                  </div>
                ),
                rowExpandable: (record) => auditView === 'day' && (record.revenue > 0 || record.fuel > 0 || record.expenses > 0)
              } : undefined}
            />
          </Card>

          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RobotOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                <span>AI Operations Analysis</span>
              </div>
            }
            open={analysisModal}
            onCancel={() => setAnalysisModal(false)}
            footer={[
              <Button key="close" onClick={() => setAnalysisModal(false)}>
                Close
              </Button>
            ]}
            width={800}
          >
            {analysisLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>Analyzing operations data with AI...</p>
              </div>
            ) : analysisData ? (
              <div>
                <Alert
                  message="Analysis Summary"
                  description={
                    <div>
                      <Row gutter={16} style={{ marginTop: 8 }}>
                        <Col span={6}>
                          <Statistic title="Revenue" value={analysisData.summary.total_revenue} prefix="$" />
                        </Col>
                        <Col span={6}>
                          <Statistic title="Expenses" value={analysisData.summary.total_fuel + analysisData.summary.total_expenses} prefix="$" />
                        </Col>
                        <Col span={6}>
                          <Statistic 
                            title="Net Profit" 
                            value={analysisData.summary.net_profit} 
                            prefix="$"
                            valueStyle={{ color: analysisData.summary.net_profit >= 0 ? '#3f8600' : '#cf1322' }}
                          />
                        </Col>
                        <Col span={6}>
                          <Statistic title="Margin" value={analysisData.summary.profit_margin} suffix="%" />
                        </Col>
                      </Row>
                    </div>
                  }
                  type="info"
                  style={{ marginBottom: 24 }}
                />
                <div style={{ maxHeight: 500, overflowY: 'auto', padding: '0 8px' }}>
                  {formatAnalysis(analysisData.analysis)}
                </div>
                <div style={{ marginTop: 16, padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#666' }}>
                  Generated: {new Date(analysisData.generated_at).toLocaleString()} | Powered by Claude 3.5 Sonnet
                </div>
              </div>
            ) : null}
          </Modal>
        </>
      )}
    </div>
  );
};

export default AuditTrail;
